import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RestTimerCircle from '../reset-between-sets/rest-timer-circle';
import CompletionGauge from '../completionGauge';
import { addTimer, deleteTimer, getTimer, timers } from '../../../utilis/helpers';
import { useWindowDimensions } from '../../../hooks';
import { screenSizes } from '../../../constants/constants';
import LoadingElement from '../../loading-element';
import tw from 'twin.macro';

const DEFAULT_SUMMARY_INTERVAL_SECONDS = 5;

// Form quality is the backend's own score for the exercise. Which key carries
// it isn't consistent across payloads (the live socket frames use `score`), so
// take the first one that's actually a number rather than betting on one name.
const resolveFormQuality = summary => {
  const candidates = [
    summary?.formQuality,
    summary?.form_quality,
    summary?.score,
    summary?.accuracy,
  ];

  const match = candidates.find(value => Number.isFinite(Number(value)) && value !== null);

  return match === undefined ? null : Math.round(Number(match));
};

// `showFormQuality` is false whenever the camera was off for this movement:
// with no pose data the backend's quality score is not a reading of anything,
// so the summary keeps only the completion-rate total.
const ConfigurationSection = ({ currentTime, summary, showFormQuality = true }) => {
  const { t } = useTranslation('exercise');
  const { exerciseName, setsCompleted, sets, completionRate, summaryInterval } = summary || {};

  const { clientWindowWidth, clientWindowHeight } = useWindowDimensions();
  // One stacked design at every size now — mobile (either orientation) scales
  // the type and spacing down so the gauge and the form-quality line have a
  // much better chance of fitting. Landscape phones are short on height rather
  // than width, but the same smaller scale (built for portrait) still helps —
  // the desktop sizing this used to fall back to on landscape ran off a
  // shallow viewport uncontrolled.
  const isDesktop = clientWindowWidth >= screenSizes.lg;
  const isPortraitMobile = !isDesktop && clientWindowHeight > clientWindowWidth;
  const isLandscapeMobile = !isDesktop && clientWindowWidth > clientWindowHeight;
  const isCompactMobile = isPortraitMobile || isLandscapeMobile;

  const comingUpTime = (Number(summaryInterval) || DEFAULT_SUMMARY_INTERVAL_SECONDS) * 1000;
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;
  const hasSummary = Boolean(summary);

  useEffect(() => {
    if (!hasSummary) {
      deleteTimer({ key: 'comingUp' });
      return undefined;
    }

    addTimer({ key: 'comingUp', value: currentTimeRef.current });
    return () => deleteTimer({ key: 'comingUp' });
  }, [hasSummary]);

  const completeTime = useMemo(
    () =>
      getTimer({
        key: 'comingUp',
        timer: currentTime,
        strategy: 'Decrease',
        totalValue: comingUpTime,
      }),
    [currentTime, comingUpTime]
  );

  const exerciseLabel = exerciseName
    ? t('configurationSection.exerciseDone', { name: exerciseName })
    : t('configurationSection.done');
  const setsLabel =
    setsCompleted && sets
      ? t('configurationSection.setsCompleted', { completed: setsCompleted, total: sets })
      : null;
  const hasCompletionRate = completionRate !== null && completionRate !== undefined;
  const formQuality = showFormQuality ? resolveFormQuality(summary) : null;

  if (!summary) {
    return (
      <div tw="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
        <LoadingElement width={60} height={60} />
        <p tw="text-xl font-semibold text-[#6d737b]">{t('configurationSection.loading')}</p>
      </div>
    );
  }

  return (
    <div
      tw="relative box-border flex h-full w-full flex-col items-center justify-center gap-6 bg-white p-10"
      // A landscape phone's shallow viewport can still be shorter than the
      // compact-scaled content — scroll instead of clipping the gauge/quality
      // line off the bottom.
      css={isCompactMobile && tw`gap-4 overflow-y-auto p-6`}
    >
      {/* Top-right countdown timer */}
      {/* completeTime/comingUpTime are tracked in ms (to match state.time) —
          RestTimerCircle's small variant renders Math.ceil(timer) directly as
          the digit, so convert to seconds here or it shows the raw ms count. */}
      <div tw="absolute top-10 right-10" css={isCompactMobile && tw`top-5 right-5`}>
        <RestTimerCircle
          timer={timers.comingUp ? completeTime / 1000 : comingUpTime / 1000}
          convertOn={comingUpTime / 1000}
        />
      </div>

      {/* Body content */}
      <div
        tw="flex w-full max-w-[600px] flex-col items-center gap-8"
        css={isCompactMobile && tw`gap-4`}
      >
        {/* Title */}
        <div tw="flex flex-col items-center gap-[9px] text-center">
          <p
            tw="m-0 text-[64px] font-bold leading-[1.2] tracking-[1.28px] text-[#475016]"
            css={isCompactMobile && tw`text-[32px] tracking-[0.64px]`}
          >
            {exerciseLabel}
          </p>
          {setsLabel && (
            <p
              tw="m-0 text-[48px] font-bold tracking-[0.8px] text-[#6d737b]"
              css={isCompactMobile && tw`text-[32px] tracking-[0.4px]`}
            >
              {setsLabel}
            </p>
          )}
        </div>

        {/* Results: the completion-rate gauge, with the backend's form-quality
            score called out underneath it as a sentence. */}
        <div tw="flex flex-col items-center gap-6" css={isCompactMobile && tw`gap-4`}>
          {hasCompletionRate && (
            <CompletionGauge
              percentage={completionRate}
              width={isCompactMobile ? 260 : 370}
              caption={t('configurationSection.completionRate')}
            />
          )}

          {/* {formQuality !== null && (
            <p
              tw="m-0 max-w-[560px] text-center text-[28px] font-medium leading-[1.35] text-[#6d737b]"
              css={isCompactMobile && tw`text-[16px]`}
            >
              {t('configurationSection.performedWith')}
              <span tw="block font-bold text-[#2e2e2e]">
                {t('configurationSection.formQualityLine', { quality: formQuality })}
              </span>
            </p>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationSection;
